import { getApiBaseUrl } from '../utils/environment.mjs';
import axios from 'axios';
import { rememberReturnPath } from '../auth/returnPath.mjs';

const API_URL = getApiBaseUrl();

class ApiService {
    constructor() {
        this.refreshPromise = null;
        this.isRefreshing = false;
        this.failedQueue = [];
        this.refreshInterval = null;
        this.rateLimitedUntil = null;
        // SECURITY: Debug mode disabled for production - set via environment variable if needed
        this.isDebugMode = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MODE === 'true';
        
        this.axiosInstance = axios.create({
            baseURL: API_URL,
            withCredentials: true, 
            headers: {
                'Content-Type': 'application/json',
            },
        });

        this.axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    if (this.isRefreshing) {
                        // If we're already refreshing, queue this request
                        return new Promise((resolve, reject) => {
                            this.failedQueue.push({ resolve, reject, originalRequest });
                        });
                    }

                    originalRequest._retry = true;
                    this.isRefreshing = true;

                    try {
                        const refreshed = await this._refreshToken();
                        if (refreshed === true) {
                            // Process the queue of failed requests
                            this._processQueue(null);
                            return this.axiosInstance(originalRequest);
                        } else if (refreshed === 'rate_limited') {
                            // Rate limited - token may still be valid, don't log out
                            this._processQueue(error);
                            return Promise.reject(error);
                        } else {
                            this._processQueue(error);
                            this._redirectToLogin();
                            return Promise.reject(error);
                        }
                    } catch (refreshError) {
                        this._processQueue(refreshError);
                        this._redirectToLogin();
                        return Promise.reject(refreshError);
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                return Promise.reject(error);
            }
        );

        // Start periodic token refresh for authenticated sessions
        this._startPeriodicRefresh();

        // Listen for page visibility changes to handle tab switching
        this._setupVisibilityHandlers();
    }

    _startPeriodicRefresh() {
     
        // Clear any existing interval
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }

        // Set up the interval for 10 minutes (600,000 ms)
        this.refreshInterval = setInterval(async () => {
            await this._performPeriodicRefresh();
        }, 10 * 60 * 1000); // 10 minutes
    }

    _stopPeriodicRefresh() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
    }

    async _performPeriodicRefresh() {
        // Don't perform periodic refresh if user is not active or if already refreshing
        if (this.isRefreshing || document.hidden) {
           
            return;
        }


        try {
            const refreshed = await this._refreshToken();
            if (refreshed === 'rate_limited') {
                // Rate limited - keep the interval running, try again next cycle
            } else if (!refreshed) {
                this._stopPeriodicRefresh();
            }
        } catch (error) {
            // SECURITY: Silently handle periodic refresh errors in production
            if (this.isDebugMode) {
                console.error('Periodic refresh error:', error.message);
            }
            this._stopPeriodicRefresh();
        }
    }

    _setupVisibilityHandlers() {
        // Handle page visibility changes
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                // Refresh token when page becomes visible again
                setTimeout(() => {
                    this._performPeriodicRefresh();
                }, 1000);
            }
        });

        // Handle page unload
        window.addEventListener('beforeunload', () => {
            this._stopPeriodicRefresh();
        });
    }

    _processQueue(error) {
        this.failedQueue.forEach(({ resolve, reject, originalRequest }) => {
            if (error) {
                reject(error);
            } else {
                resolve(this.axiosInstance(originalRequest));
            }
        });
        
        this.failedQueue = [];
    }

    _redirectToLogin() {
        // Stop periodic refresh when redirecting to login
        this._stopPeriodicRefresh();
        
        // Add a small delay to prevent multiple redirects
        setTimeout(() => {
            if (window.location.pathname !== '/login') {
                // Signing in again brings them back to this page.
                rememberReturnPath(window.location.pathname + window.location.search);
                window.location.href = '/login';
            }
        }, 100);
    }

    async makeRequest(url, options = {}) {
        try {
            const response = await this.axiosInstance({
                url,
                ...options,
            });
            return response;
        } catch (error) {
            // SECURITY: Don't log request details in production
            if (this.isDebugMode) {
                console.error('API request failed:', error.message);
            }
            throw error;
        }
    }

    // Convenience methods for different HTTP methods
    async get(url, config = {}) {
        return this.makeRequest(url, { method: 'GET', ...config });
    }

    async post(url, data = {}, config = {}) {
        return this.makeRequest(url, { 
            method: 'POST', 
            data, 
            ...config 
        });
    }

    async put(url, data = {}, config = {}) {
        return this.makeRequest(url, { 
            method: 'PUT', 
            data, 
            ...config 
        });
    }

    async delete(url, config = {}) {
        return this.makeRequest(url, { method: 'DELETE', ...config });
    }

    async _refreshToken() {
        // Prevent multiple simultaneous refresh requests
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = this._performRefresh();
        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.refreshPromise = null;
        }
    }

    async _performRefresh() {
        // Short-circuit if we already know we're rate-limited - avoids hammering the endpoint
        if (this.rateLimitedUntil && Date.now() < this.rateLimitedUntil) {
            return 'rate_limited';
        }

        try {
            // Use the same axios instance but without interceptors for refresh
            const response = await axios.post(
                `${API_URL}/auth/refresh-token`,
                {},
                {
                    withCredentials: true,
                    timeout: 10000
                }
            );

            if (response.status === 200 && response.data) {
                const data = response.data;
                this.rateLimitedUntil = null; // clear any previous rate-limit state on success

                // Dispatch user update event
                window.dispatchEvent(new CustomEvent('userUpdated', {
                    detail: data.user
                }));

                return true;
            }

            return false;
        } catch (error) {
            // Rate-limited: back off for the full 15-minute window, don't log out
            if (error.response?.status === 429) {
                this.rateLimitedUntil = Date.now() + 15 * 60 * 1000;
                return 'rate_limited';
            }
            // SECURITY: Log minimal error info only in debug mode
            if (this.isDebugMode) {
                console.error('Token refresh failed:', error.message);
            }
            return false;
        }
    }

    // Method to manually clear auth state
    clearAuth() {
        this.isRefreshing = false;
        this.refreshPromise = null;
        this.failedQueue = [];
        this.rateLimitedUntil = null;
        this._stopPeriodicRefresh();
    }

    // Method to manually trigger refresh (for testing)
    async manualRefresh() {
   
        return await this._refreshToken();
    }

    // Method to toggle debug mode - no-op outside development builds
    setDebugMode(enabled) {
        if (process.env.NODE_ENV !== 'development') {
            return;
        }
        this.isDebugMode = enabled;
        if (enabled) {
            this._startPeriodicRefresh();
        } else {
            this._stopPeriodicRefresh();
        }
    }

    // Method to get refresh status
    getRefreshStatus() {
        return {
            isDebugMode: this.isDebugMode,
            isRefreshing: this.isRefreshing,
            hasInterval: !!this.refreshInterval,
            queueLength: this.failedQueue.length
        };
    }
}

export const apiService = new ApiService();

// SECURITY: Global window exposure removed for production
// Debug access should only be enabled in development via browser extensions or dev tools
